<template>
    <div class="mt-n6">
        <v-container class="py-16">
            <v-row>
                <v-col md="7" cols="12">
                    <div class="d-block mt-md-10 mb-md-10 mt-4 mb-6" style="text-align: left">
                        <label class="d-block yellow--text text--darken-3 font-weight-bold">ติดต่อเรา</label>
                        <hr class="rounded" />
                        <label class="d-block white--text font-weight-bold mb-2">{{ contact.companyName }}</label>
                        <div class="contact-line">
                            <v-icon small color="yellow darken-3">mdi-map-marker</v-icon>
                            <span>สำนักงานใหญ่: {{ contact.address }}
                                <a class="map-link" :href="mapUrl(contact.address)" target="_blank" rel="noopener">ดูแผนที่</a>
                            </span>
                        </div>
                        <div class="contact-line">
                            <v-icon small color="yellow darken-3">mdi-email</v-icon>
                            <span>อีเมล: <a :href="'mailto:' + contact.email">{{ contact.email }}</a></span>
                        </div>
                        <div class="contact-line">
                            <v-icon small color="yellow darken-3">mdi-phone</v-icon>
                            <span>โทร:
                                <template v-for="(phone, i) of phones(contact.mobile)">
                                    <span v-if="i" :key="'sep' + i">, </span>
                                    <a :key="phone" :href="'tel:' + phone.replace(/-/g, '')">{{ phone }}</a>
                                </template>
                            </span>
                        </div>
                    </div>
                    <!-- จอใหญ่: การ์ดสาขาเป็นตาราง แผนที่ติดอยู่ด้านขวา -->
                    <template v-if="!isMobile">
                        <label class="d-block white--text font-weight-bold" style="text-align: left">สาขาทั้งหมดของเรา</label>
                        <label class="d-block grey--text text-caption mb-4" style="text-align: left">เลือกการ์ดสาขาเพื่อดูตำแหน่งบนแผนที่</label>
                        <v-row>
                            <v-col v-for="l of branches" :key="l.name" cols="12" md="6">
                                <branch-card :branch="l" :selected="l.name === selectedBranch" @select="selectBranch"></branch-card>
                            </v-col>
                        </v-row>
                    </template>
                </v-col>
                <v-col md="5" cols="12">
                    <template v-if="isMobile">
                        <label class="d-block white--text font-weight-bold" style="text-align: left">สาขาทั้งหมดของเรา</label>
                        <label class="d-block grey--text text-caption" style="text-align: left">แตะสาขาหรือจังหวัดบนแผนที่เพื่อดูตำแหน่ง</label>
                    </template>
                    <div class="map-wrapper">
                        <branch-map class="branch-map" :branches="branches" :selected="selectedBranch" @select="onMapSelect"></branch-map>
                        <transition name="caption-fade" mode="out-in">
                            <div v-if="selected" :key="selected.name" class="map-caption">
                                <v-icon small color="yellow darken-3">mdi-map-marker-radius</v-icon>
                                <span class="yellow--text text--darken-3 font-weight-bold">สาขา{{ selected.name }}</span>
                                <span class="grey--text text--lighten-2">· {{ regionLabel(selected.region) }}</span>
                            </div>
                            <div v-else-if="!isMobile" key="hint" class="map-caption grey--text">คลิกการ์ดสาขาหรือจังหวัดบนแผนที่</div>
                        </transition>
                    </div>
                    <!-- จอเล็ก: การ์ดสาขาเป็นแถบปัดซ้าย-ขวาใต้แผนที่ เลือกแล้วเห็นแผนที่ทันทีไม่ต้องเลื่อนหน้า -->
                    <div v-if="isMobile" ref="strip" class="branch-strip">
                        <div v-for="l of branches" :key="l.name" :ref="'strip-' + l.name" class="branch-strip__item">
                            <branch-card :branch="l" :selected="l.name === selectedBranch" @select="selectBranch"></branch-card>
                        </div>
                    </div>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
import axios from 'axios';
import BranchCard from '../components/BranchCard.vue';
import BranchMap from '../components/BranchMap.vue';
import { BRANCH_REGIONS, regionOf } from '../utils/branch-regions';

// ข้อมูลตั้งต้น: API /contents/contact-us ยังคืนค่าว่าง ({"branches":[]}) ทำให้หน้านี้ไม่มีข้อมูล
// ถ้าเพิ่มข้อมูลในระบบหลังบ้านแล้ว ค่าจาก API จะถูกใช้แทน
const DEFAULT_CONTACT = {
    companyName: 'บริษัท มูฟไว จำกัด',
    address: '66/7-9 ซอยสุขาภิบาล 2 ซอย 31 แขวงดอกไม้ เขตประเวศ กรุงเทพมหานคร 10250',
    email: 'movevai.th@gmail.com',
    mobile: '02-613-0885, 099-5098405',
    // lat/lng โดยประมาณ ใช้วางหมุดสาขาตอนซูมแผนที่
    branches: [
        { name: 'สีลม', address: '32/7-8 ถนนสุรศักดิ์ แขวงสีลม เขตบางรัก กรุงเทพมหานคร 10500', lat: 13.7203, lng: 100.5219 },
        { name: 'วังบูรพา', address: '857 ถนนมหาไชย แขวงวังบูรพาภิรมย์ เขตพระนคร กรุงเทพมหานคร 10200', lat: 13.7462, lng: 100.5010 },
        { name: 'เชียงใหม่', address: '8/14 ถนนเชียงใหม่-ลำปาง ตำบลวัดเกต อำเภอเมือง จังหวัดเชียงใหม่ 50000', lat: 18.7960, lng: 99.0050 },
        { name: 'พิษณุโลก', address: '198/17-18 ถนนเอกาทศรฐ ตำบลในเมือง อำเภอเมือง จังหวัดพิษณุโลก 65000', lat: 16.8250, lng: 100.2650 },
        { name: 'สุราษฎร์ธานี', address: '191/5 ถนนชนเกษม ตำบลตลาด อำเภอเมือง จังหวัดสุราษฎร์ธานี 84000', lat: 9.1390, lng: 99.3260 }
    ]
};

export default {
    data: () => ({
        contact: { ...DEFAULT_CONTACT },
        selectedBranch: null
    }),
    computed: {
        branches(){
            return (this.contact.branches || []).map(b => ({ ...b, region: regionOf(b.address) }));
        },
        selected(){
            return this.branches.find(b => b.name === this.selectedBranch) || null;
        },
        isMobile(){
            return this.$vuetify.breakpoint.smAndDown;
        }
    },
    watch: {
        // เลือกจากหมุดบนแผนที่ก็เลื่อนแถบการ์ดให้การ์ดที่เลือกอยู่ตรงกลาง
        selectedBranch(name){
            if (name && this.isMobile) this.$nextTick(() => this.scrollStripTo(name));
        }
    },
    async mounted(){
        try{
            const { data } = await axios.get(process.env.VUE_APP_BASE_URL + '/contents/contact-us');
            const merged = { ...DEFAULT_CONTACT };
            for (const key of ['companyName', 'address', 'email', 'mobile']) {
                if (data && data[key]) merged[key] = data[key];
            }
            if (data && Array.isArray(data.branches) && data.branches.length) merged.branches = data.branches;
            this.contact = merged;
        }catch(error){
            // โหลดไม่ได้ก็ยังแสดงข้อมูลตั้งต้นได้ ไม่ต้องเด้ง error
        }
    },
    methods: {
        phones(mobile){
            return (mobile || '').split(',').map(p => p.trim()).filter(Boolean);
        },
        mapUrl(address){
            return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(address || '');
        },
        // คลิกการ์ดเดิมซ้ำ = ยกเลิกการเลือกและซูมออก
        selectBranch(name){
            this.selectedBranch = this.selectedBranch === name ? null : name;
        },
        scrollStripTo(name){
            const strip = this.$refs.strip;
            const item = (this.$refs['strip-' + name] || [])[0];
            if (!strip || !item) return;
            // เลื่อนเฉพาะแนวนอนของแถบ (scrollIntoView จะเลื่อนทั้งหน้าในแนวตั้งด้วย)
            strip.scrollTo({ left: item.offsetLeft - (strip.clientWidth - item.clientWidth) / 2, behavior: 'smooth' });
        },
        onMapSelect(name){
            this.selectedBranch = name;
        },
        regionLabel(region){
            const found = BRANCH_REGIONS.find(r => r.key === region);
            return found ? found.label : '';
        }
    },
    components: {
        BranchCard,
        BranchMap
    }
};
</script>


<style scoped>
  hr.rounded {
    border-top: 1px solid #C49C4F;
    border-radius: 5px;
    margin-top: 12px;
    margin-bottom: 12px;
  }
  .contact-line {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 6px;
    color: #e0e0e0;
  }
  .contact-line .v-icon {
    margin-top: 3px;
  }
  .contact-line a {
    color: #e0e0e0;
  }
  .map-link {
    color: #C49C4F !important;
    font-size: 0.875rem;
    text-decoration: none;
    margin-left: 4px;
  }
  .map-link:hover {
    text-decoration: underline;
  }
  .map-wrapper {
    position: sticky;
    top: 96px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 40px;
  }
  .branch-map {
    /* จำกัดด้วยความกว้าง (ไม่ใช่ max-height) เพื่อให้หมุดที่วางทับ SVG ตรงตำแหน่ง
       0.539 = สัดส่วนกว้าง/สูงของ map.svg ทำให้แผนที่ทั้งใบพอดีจอ */
    width: min(100%, 420px, calc((100vh - 220px) * 0.539));
  }
  .branch-strip {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    /* เผื่อขอบให้เงาการ์ดที่เลือกไม่โดนตัด และให้การ์ดถัดไปโผล่ให้รู้ว่าปัดได้ */
    margin: 4px -12px 0;
    padding: 8px 12px 12px;
    scrollbar-width: none;
  }
  .branch-strip::-webkit-scrollbar {
    display: none;
  }
  .branch-strip__item {
    flex: 0 0 80%;
    max-width: 320px;
    scroll-snap-align: center;
  }
  @media (max-width: 959px) {
    .map-wrapper {
      position: static;
      padding-top: 12px;
    }
    .branch-map {
      /* เหลือที่ให้แถบการ์ดใต้แผนที่โผล่ในจอเดียวกัน */
      width: min(100%, 360px, calc((100vh - 330px) * 0.539));
      min-width: 200px;
    }
  }
  .map-caption {
    display: flex;
    align-items: center;
    gap: 4px;
    min-height: 28px;
    margin-top: 12px;
    font-size: 0.9rem;
    text-align: center;
  }
  .caption-fade-enter-active,
  .caption-fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }
  .caption-fade-enter,
  .caption-fade-leave-to {
    opacity: 0;
    transform: translateY(4px);
  }
</style>
